import { Component, OnInit, inject, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { first } from 'rxjs';
import { AxiosError } from 'axios';
import { SchemaArrayFieldEditorDialogComponent } from 'src/app/helper/schema-array-field-editor-dialog/schema-array-field-editor-dialog.component';
import {
  EnumsService,
  ListEnumsResponse,
} from 'src/app/services/enums/enums.service';
import { Schema } from 'src/types';
import { z } from 'zod';
import {
  EnumDescriptor,
  EnumDescriptorColumn,
  enumDescriptors,
} from './enum-descriptors';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { CustomInputFieldComponent } from '../../custom-input-field/custom-input-field.component';
import { CustomCheckboxComponent } from '../../custom-checkbox/custom-checkbox.component';
import { MatIcon } from '@angular/material/icon';
import { PayeeButtonComponent } from '../../../shared/payee-buttons/payee-button/payee-button.component';
import { PayeeDropdownComponent } from '../../../shared/payee-form-fields/payee-dropdown/payee-dropdown.component';
import { PayeeMiniIconButtonComponent } from '../../../shared/payee-buttons/payee-mini-icon-button/payee-mini-icon-button.component';
import { PayeeIconComponent } from '../../../shared/payee-icon/payee-icon.component';

type EnumItem = Record<string, unknown> & {
  id?: unknown;
  label?: unknown;
  name?: unknown;
};

type SelectedEnum = {
  label: string;
  model: string;
  items: EnumItem[];
  descriptor: EnumDescriptor;
};

@Component({
  selector: 'app-enums',
  templateUrl: './enums.component.html',
  styleUrls: ['./enums.component.scss'],
  imports: [
    MatButton,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    CustomInputFieldComponent,
    CustomCheckboxComponent,
    MatIconButton,
    MatIcon,
    PayeeButtonComponent,
    PayeeDropdownComponent,
    PayeeMiniIconButtonComponent,
    PayeeIconComponent,
  ],
})
export class EnumsComponent implements OnInit {
  private dialog = inject(MatDialog);
  private enumsService = inject(EnumsService);
  private snackbar = inject(MatSnackBar);

  enumList: ListEnumsResponse['enums'] = [];
  enums: Record<string, (unknown & { id: unknown; })[] | null> = {};
  readonly selectedEnum = signal<SelectedEnum | null>(null);

  country: string | null = null;
  hasChanged = false;
  pageSize = 10;
  readonly pageSizeOptions = [5, 10, 25, 100];
  page = 0;

  readonly enumLabel = (item: ListEnumsResponse['enums'][number] | null) =>
    item?.label ?? '';

  get selectedEnumOption() {
    const selected = this.selectedEnum();
    if (!selected) {
      return null;
    }
    return (
      this.enumList.find((item) => item.model === selected.model) ?? null
    );
  }

  async ngOnInit() {
    const enumList = await this.enumsService.listEnums();
    this.enumList = enumList.enums;
    if (this.enumList[0]) {
      await this.selectEnum(this.enumList[0]);
    }
  }

  async selectEnum(enumItem: (typeof this.enumList)[number]) {
    if (!enumItem?.model) {
      return;
    }

    const res = await this.enumsService.getEnum(enumItem.model);
    this.enums[enumItem.model] = res;

    const descriptor = enumDescriptors.find((d) => d.model === enumItem.model);

    if (!descriptor) {
      this.snackbar.open('Hiányzik az enum leírója!', 'OK', {
        duration: 5000,
      });
      return;
    }

    await Promise.all(
      descriptor.columns.map(async (column) => {
        if (column.type !== 'external') {
          return;
        }

        if (!column.externalModel) {
          this.snackbar.open(
            `Hiányzik az enum külső hivatkozása ${column.name}`,
            'OK',
            {
              duration: 5000,
            },
          );
          return;
        }

        if (column.externalModel in this.enums) {
          return;
        }

        this.enums[column.externalModel] = null;
        const external = await this.enumsService.getEnum(column.externalModel);
        this.enums[column.externalModel] = external;
      }),
    );

    this.selectedEnum.set({
      items: res,
      label: enumItem.label,
      model: enumItem.model,
      descriptor,
    });

    this.fixPage();
  }

  addEnumElement() {
    if (!this.selectedEnum()) {
      return;
    }

    const emptyElement: Record<string, unknown> = {};
    Object.values(this.selectedEnum()!.descriptor.columns).forEach((key) => {
      if (key.type === 'enum') {
        emptyElement[key.name] = key.values?.[0] ?? null;
      } else if (key.type === 'array') {
        emptyElement[key.name] = [];
      } else if (key.type === 'boolean') {
        emptyElement[key.name] = false;
      } else {
        emptyElement[key.name] = null;
      }
    });
    this.selectedEnum()!.items.push(emptyElement);
    this.page = Math.floor(
      (this.selectedEnum()!.items.length - 1) / this.pageSize,
    );
    this.hasChanged = true;
  }

  updateSchemaElement(value: unknown, index: number, fieldName: string) {
    this.selectedEnum()!.items[index][fieldName] = value;
    this.hasChanged = true;
  }

  getLabel(item: EnumItem) {
    return item.label || item.name || item.id;
  }

  getBooleanValue(schemaRow: EnumItem, fieldName: string): boolean {
    return schemaRow[fieldName] === true;
  }

  getExternalLabel(row: EnumItem, column: EnumDescriptorColumn): string {
    const id = row[column.name];
    if (!column.externalModel) {
      return String(id ?? '');
    }

    const external = this.enums[column.externalModel];
    if (!external) {
      return String(id ?? '');
    }

    const externalItem = external.find((item) => item.id === id);
    if (!externalItem) {
      return String(id ?? '');
    }

    return String(this.getLabel(externalItem as EnumItem));
  }

  deleteSchemaElement(index: number) {
    if (!this.selectedEnum()) {
      return;
    }

    this.selectedEnum()!.items.splice(index, 1);
    this.hasChanged = true;

    this.fixPage();
  }

  async saveSchema() {
    if (!this.selectedEnum()) {
      return;
    }

    try {
      await this.enumsService.updateSchema({
        items: this.selectedEnum()!.items,
        model: this.selectedEnum()!.model,
      });
      await this.selectEnum(this.selectedEnum()!);
      this.hasChanged = false;
    } catch (error) {
      let message = 'Valami hiba történt mentéskor';
      if (error instanceof AxiosError && error.response?.data.message) {
        message = error.response.data.message;
      }
      console.error('Error while saving schema', error);
      this.snackbar.open(message, 'OK', {
        duration: 5000,
      });
    }
  }

  getPaginatedSchema() {
    if (!this.selectedEnum()) {
      return [];
    }
    return this.selectedEnum()!.items.slice(
      this.page * this.pageSize,
      (this.page + 1) * this.pageSize,
    );
  }

  prevPage() {
    if (this.page > 0) {
      --this.page;
    }
  }

  nextPage() {
    if (!this.selectedEnum()) {
      return;
    }
    if (
      this.page * this.pageSize + this.pageSize <
      this.selectedEnum()!.items.length
    ) {
      ++this.page;
    }
  }

  setPageSize(size: number) {
    this.pageSize = size;
    this.fixPage();
  }

  min(val1: number, val2: number) {
    return Math.min(val1, val2);
  }

  downloadselectedEnum() {
    if (!this.selectedEnum()) {
      return;
    }

    const file = new Blob([JSON.stringify(this.selectedEnum())], {
      type: 'application/json',
    });
    const a = document.createElement('a');
    const url = URL.createObjectURL(file);
    a.href = url;
    a.download = this.selectedEnum()!.label;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }, 0);
  }

  async discardChanges() {
    if (!this.selectedEnum()) {
      return;
    }

    await this.selectEnum({
      label: this.selectedEnum()!.label,
      model: this.selectedEnum()!.model,
    });

    this.hasChanged = false;
  }

  editArray(schemaRow: EnumItem, fieldName: string) {
    const dialog = this.dialog.open(SchemaArrayFieldEditorDialogComponent, {
      data: {
        schemaRow,
        fieldName,
      },
    });

    dialog
      .afterClosed()
      .pipe(first())
      .subscribe({
        next: (result) => {
          if (result) {
            schemaRow[fieldName] = result;
            this.hasChanged = true;
          }
        },
      });
  }

  getArraySummary(schemaRow: EnumItem, fieldName: string): string {
    const array = schemaRow[fieldName];
    if (!Array.isArray(array) || array.length === 0) {
      return 'Üres a tömb';
    }

    if (array.length === 1) {
      return `"${array[0] || '<Üres elem>'}"`;
    }

    return `"${array[0] || '<Üres elem>'}" + ${array.length - 1}`;
  }

  async importSchema(files: FileList) {
    if (!this.selectedEnum() || files.length === 0) {
      return;
    }
    const file = files.item(0) as File;
    try {
      const text = await file.text();

      const content = JSON.parse(text) as Schema<unknown>;
      const schema = this.getSchemaOfSelectedEnum();
      const validated = schema.parse(content);
      this.selectedEnum()!.items = validated.items;
      this.hasChanged = true;
      this.snackbar.open('Sikeres import!', 'OK', {
        duration: 5000,
      });
    } catch (error) {
      console.error(error);
      this.snackbar.open('Sikertelen import!', 'OK', { duration: 5000 });
    }
  }

  private fixPage() {
    if (!this.selectedEnum()) {
      this.page = 0;
      return;
    }
    while (
      this.page > 0 &&
      this.page * this.pageSize >= this.selectedEnum()!.items.length
    ) {
      --this.page;
    }
  }

  private getSchemaOfSelectedEnum(): z.ZodSchema {
    const itemSchema = z.object(
      this.selectedEnum()!.descriptor.columns.reduce((zodObject, column) => {
        let columnSchema: z.ZodSchema;
        switch (column.type) {
          case 'array':
            columnSchema = z.array(z.string());
            break;
          case 'boolean':
            columnSchema = z.boolean();
            break;
          case 'enum':
            columnSchema = z.enum(column.values as [string, ...string[]]);
            break;
          case 'external':
          case 'int':
            columnSchema = z.number().int();
            break;
          case 'string':
            columnSchema = z.string();
            break;
        }

        return {
          ...zodObject,
          [column.name]: columnSchema,
        };
      }, {} as z.ZodRawShape),
    );

    return z.object({
      label: z.string(),
      model: z.string(),
      descriptor: z.any(),
      items: z.array(itemSchema),
    });
  }
}
